"use client"

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react"

/* ------------------------------------------------------------------
   One shared measuring pass for every animated block on the page.

   An IntersectionObserver only reports threshold crossings, so jumping
   from the foot of the page back to the top never tells an element it
   is off screen again and the sequence will not replay. Measuring real
   positions on each scroll frame is exact, and one rAF-throttled pass
   for all blocks costs less than an observer each.
   ------------------------------------------------------------------ */

type Tracked = {
  el: HTMLElement
  set: (visible: boolean) => void
  visible: boolean
  enterAt: number
}

const tracked = new Set<Tracked>()
let frame = 0
let listening = false

function measure() {
  frame = 0
  const vh = window.innerHeight
  for (const item of tracked) {
    const rect = item.el.getBoundingClientRect()
    const entered = rect.top < vh * item.enterAt && rect.bottom > 0
    const belowFold = rect.top >= vh
    if (entered && !item.visible) {
      item.visible = true
      item.set(true)
    } else if (belowFold && item.visible) {
      // Reset only once it is back under the fold, so scrolling down
      // past a block never makes it fade out beneath you.
      item.visible = false
      item.set(false)
    }
  }
}

function schedule() {
  if (!frame) frame = requestAnimationFrame(measure)
}

function track(item: Tracked) {
  tracked.add(item)
  if (!listening) {
    listening = true
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)
  }
  schedule()
  return () => {
    tracked.delete(item)
  }
}

/** True while the element is on screen, false once it is back below the fold. */
export function useInViewRepeat<T extends HTMLElement>(enterAt = 0.95, initial = false) {
  const ref = useRef<T | null>(null)
  const [shown, setShown] = useState(initial)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true)
      return
    }
    return track({ el, set: setShown, visible: initial, enterAt })
  }, [enterAt, initial])

  return { ref, shown }
}

/**
 * Wraps a group. Children carrying .reveal, .reveal-clip, .reveal-rule or
 * .reveal-plate animate together, staggered by their own --d.
 * `atLoad` is for the first screen: it is rendered already shown and plays
 * its entrance in CSS on first paint, without waiting for JavaScript, so the
 * largest element on screen paints at once.
 */
export function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
  style,
  atLoad = false,
}: {
  children: ReactNode
  delay?: number
  className?: string
  as?: "div" | "section" | "li" | "article" | "header"
  style?: CSSProperties
  atLoad?: boolean
}) {
  const { ref, shown } = useInViewRepeat<HTMLElement>(0.95, atLoad)

  return (
    <Tag
      ref={ref as never}
      className={`${shown ? "shown " : ""}${atLoad ? "reveal-at-load " : ""}${className}`}
      style={{ ["--d" as string]: `${delay}ms`, ...style }}
    >
      {children}
    </Tag>
  )
}

/** Counts to `target` every time it comes back into view. */
export function CountUp({
  target,
  suffix,
  duration = 900,
}: {
  target: number
  suffix: string
  duration?: number
}) {
  const { ref, shown } = useInViewRepeat<HTMLSpanElement>(0.99)
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!shown) {
      setValue(0)
      return
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(target)
      return
    }
    let raf = 0
    let start = 0
    const tick = (now: number) => {
      if (!start) start = now
      const p = Math.min(1, (now - start) / duration)
      setValue(Math.round((1 - Math.pow(1 - p, 3)) * target))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    const settle = setTimeout(() => setValue(target), duration + 60)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(settle)
    }
  }, [shown, target, duration])

  return (
    <span ref={ref} className="tabular-nums">
      {value}
      {suffix}
    </span>
  )
}
